var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { cx, css as emotionCss } from 'emotion';
import ReactDOM from 'react-dom';
import { FixedSizeList } from 'react-window';
import { inputChange, useCitrusForm } from '@citrus/form';
import { Input } from '@citrus/input';
import { getAutomation, getStyleNames, useFormInputId, useUpdateValidation, useUserManagedValidation, useValidationOnMount, } from '@citrus/runtime';
import { isArray, isNilOrEmpty, isNotNilOrEmpty, isString } from '@pcty/utils';
import { useTheme } from '@citrus/user';
import { ACTIVATE_BUTTONS as activateButtons, CLOSE_BUTTONS as closeButtons, DIRECTION_ARROWS as directionArrows, DOWN_BUTTONS as downKeys, SPACE_BUTTON as spaceKeys, UP_BUTTONS as upKeys, } from '../constants';
import { DropdownListContainer, InputBaseMainContainerStyles } from '../../Select.styled';
// sub-functions
import { getListItemData, handleListScroll, handleScrollToItemOnOpen, resetFocusRow } from '../utils/dropdownList';
import { _handleInputFocused, _handleInputOnSearch, resetInputValue } from '../utils/inputFunctions';
import { flattenOptionsArray, getValueFromFlatArray } from '../utils/flattenedOptions';
import { getOptionsOnlyFixed, getOptionsWithoutFixed } from '../utils/getOptions';
import { useKeyPress } from '../utils/useKeyPress';
import { dropdownListShadowStyles, dropdownListShadowSelectAllStyles } from '../../../styles/legacy';
import { dropdownListShadowStyles as dropdownListShadowStylesCitrus2022, dropdownListShadowSelectAllStyles as dropdownListShadowSelectAllStylesCitrus2022, } from '../../../styles/citron';
import { isEqual } from 'lodash';
import { handleOptionsArrays } from '../utils/handleOptionsArrays';
import { handleOnChange } from '../utils/handleOnChange';
import { handleRowClicked } from '../utils/itemSelection/handleRowClicked';
// sub-components
import DropdownIcon from './DropdownIcon';
import FixedSizeGridRow from './FixedSizeGridRow';
import FixedSizeListRow from './FixedSizeListRow';
import InputBase from './InputBase';
import MultiTagsElement from './MultiTags';
import SelectAllButton from './SelectAllElement';
var VERSION = 'unknown';
var DEBUG = false;
var InputSelectBase = function (_a) {
    var _b;
    var _c = _a.actionIconSeparator, actionIconSeparator = _c === void 0 ? true : _c, ariaLabel = _a["aria-label"], automationId = _a.automationId, className = _a.className, classNameWrapper = _a.classNameWrapper, clearInputOnFocus = _a.clearInputOnFocus, _d = _a.closeListOnBlur, closeListOnBlur = _d === void 0 ? !DEBUG : _d, _e = _a.closeListOnSelect, closeListOnSelect = _e === void 0 ? !DEBUG : _e, _f = _a.debounceTimeout, debounceTimeout = _f === void 0 ? 300 : _f, disabled = _a.disabled, _g = _a.dropdownLocation, dropdownLocation = _g === void 0 ? 'bottom' : _g, _h = _a.dropdownLocationBoundary, dropdownLocationBoundary = _h === void 0 ? 0 : _h, emptyLabel = _a.emptyLabel, _j = _a.enableSelectAll, enableSelectAll = _j === void 0 ? false : _j, errorMessage = _a.errorMessage, footnote = _a.footnote, _k = _a.hideOptionalLabel, hideOptionalLabel = _k === void 0 ? false : _k, id = _a.id, _l = _a.initialScrollOffset, initialScrollOffset = _l === void 0 ? 0 : _l, _m = _a.isLoading, isLoading = _m === void 0 ? false : _m, _o = _a.isMulti, isMulti = _o === void 0 ? false : _o, _p = _a.isScrollingPlaceholder, isScrollingPlaceholder = _p === void 0 ? false : _p, isScrollingPlaceholderText = _a.isScrollingPlaceholderText, label = _a.label, labelAddOn = _a.labelAddOn, labelPosition = _a.labelPosition, _q = _a.maxLength, maxLength = _q === void 0 ? 250 : _q, _r = _a.openOnMouseDown, openOnMouseDown = _r === void 0 ? true : _r, onBlur = _a.onBlur, onChange = _a.onChange, onCreatedItem = _a.onCreatedItem, onInputFocused = _a.onInputFocused, onSearch = _a.onSearch, onScrolledToBottom = _a.onScrolledToBottom, propsOptions = _a.options, persistentSearch = _a.persistentSearch, primaryKey = _a.primaryKey, _s = _a.portal, portal = _s === void 0 ? true : _s, placeholder = _a.placeholder, readOnly = _a.readOnly, required = _a.required, _t = _a.scrolledToBottomItemBuffer, scrolledToBottomItemBuffer = _t === void 0 ? 0 : _t, _u = _a.scrollToItemOnOpen, scrollToItemOnOpen = _u === void 0 ? false : _u, _v = _a.showOnlySelected, showOnlySelected = _v === void 0 ? false : _v, selectAllOptionText = _a.selectAllOptionText, deselectAllOptionText = _a.deselectAllOptionText, selectFilteredOptionText = _a.selectFilteredOptionText, deselectFilteredOptionText = _a.deselectFilteredOptionText, _w = _a.rowHeight, rowHeight = _w === void 0 ? 35 : _w, _x = _a.showClearButton, showClearButton = _x === void 0 ? false : _x, _y = _a.showRequiredLabel, showRequiredLabel = _y === void 0 ? false : _y, showValidationState = _a.showValidationState, _z = _a.searchable, searchable = _z === void 0 ? false : _z, _0 = _a.searchKeys, searchKeys = _0 === void 0 ? ['label'] : _0, _1 = _a.tabIndex, tabIndex = _1 === void 0 ? 0 : _1, tooltip = _a.tooltip, _2 = _a.__TYPE, __TYPE = _2 === void 0 ? 'InputSelectBase' : _2, valid = _a.valid, validate = _a.validate, propsValue = _a.value, wrapperStyles = _a.wrapperStyles, _3 = _a.__isEmployee, __isEmployee = _3 === void 0 ? false : _3, _4 = _a.optionsToDisplay, optionsToDisplay = _4 === void 0 ? 7 : _4, renderItem = _a.renderItem, props = __rest(_a, ["actionIconSeparator", 'aria-label', "automationId", "className", "classNameWrapper", "clearInputOnFocus", "closeListOnBlur", "closeListOnSelect", "debounceTimeout", "disabled", "dropdownLocation", "dropdownLocationBoundary", "emptyLabel", "enableSelectAll", "errorMessage", "footnote", "hideOptionalLabel", "id", "initialScrollOffset", "isLoading", "isMulti", "isScrollingPlaceholder", "isScrollingPlaceholderText", "label", "labelAddOn", "labelPosition", "maxLength", "openOnMouseDown", "onBlur", "onChange", "onCreatedItem", "onInputFocused", "onSearch", "onScrolledToBottom", "options", "persistentSearch", "primaryKey", "portal", "placeholder", "readOnly", "required", "scrolledToBottomItemBuffer", "scrollToItemOnOpen", "showOnlySelected", "selectAllOptionText", "deselectAllOptionText", "selectFilteredOptionText", "deselectFilteredOptionText", "rowHeight", "showClearButton", "showRequiredLabel", "showValidationState", "searchable", "searchKeys", "tabIndex", "tooltip", "__TYPE", "valid", "validate", "value", "wrapperStyles", "__isEmployee", "optionsToDisplay", "renderItem"]);
    var citrus2022RowPadding = 4;
    var dropDownListContainerSuffix = 'dropdown-list-container';
    var dropDownListContainerId = "".concat(id, "-").concat(dropDownListContainerSuffix);
    var isRequired = isString(required) ? true : required !== null && required !== void 0 ? required : false;
    var requiredMessage = isString(required) ? required : undefined;
    var isCitron = useTheme().citrus2022;
    var _5 = useCitrusForm(), form = _5.form, dispatch = _5.dispatch, customValidate = _5.validate, formDisabled = _5.disabled;
    var _6 = useState(false), filterTyping = _6[0], setFilterTyping = _6[1];
    var _7 = useState(getOptionsOnlyFixed(propsOptions)), fixedOptions = _7[0], setFixedOptions = _7[1];
    var _8 = useState(null), focusedRow = _8[0], setFocusedRow = _8[1];
    var _9 = useState(DEBUG), isOpen = _9[0], setIsOpen = _9[1];
    var _10 = useState(false), inputFocused = _10[0], setInputFocused = _10[1];
    var _11 = useState(''), inputValue = _11[0], setInputValue = _11[1];
    var _12 = useState(''), persistedInputValue = _12[0], setPersistedInputValue = _12[1];
    var _13 = useState(false), isLoaded = _13[0], setIsLoaded = _13[1];
    var _14 = useState([]), listDataArray = _14[0], setListDataArray = _14[1];
    var _15 = useState(false), listDataArraySwitch = _15[0], setListDataArraySwitch = _15[1];
    var _16 = useState(false), openScroll = _16[0], setOpenScroll = _16[1];
    var _17 = useState(propsOptions), options = _17[0], setOptions = _17[1];
    var _18 = useState(false), scrollLoading = _18[0], setScrollLoading = _18[1];
    var _19 = useState(propsValue), value = _19[0], setValue = _19[1];
    var _20 = useState({}), listContainerStyles = _20[0], setListContainerStyles = _20[1];
    var _21 = useState([]), flattenedOptions = _21[0], setFlattenedOptions = _21[1];
    var _22 = useState([]), optionsDataArrayWithoutFixed = _22[0], setOptionsDataArrayWithoutFixed = _22[1];
    var _23 = useState(false), isActivated = _23[0], setIsActivated = _23[1];
    var _24 = useState({ valid: false }), _validation = _24[0], setValidation = _24[1];
    var _25 = useState(dropdownLocation), _dropdownLocation = _25[0], setDropdownLocation = _25[1];
    var formInputId = useFormInputId(id);
    var userManagedValidation = useUserManagedValidation(valid, errorMessage, showValidationState, setValidation);
    var wrapperRef = useRef(null);
    var gridListRef = useRef(null);
    var inputSelectContainer = useRef(null);
    var listRef = useRef(null);
    var scrollingListInnerRef = useRef(null);
    var scrollingListOuterRef = useRef(null);
    var selectListRef = useRef(null);
    var textInputRef = useRef(null);
    var timeoutId = useRef(null);
    var actionArea = useRef(null);
    var isFocusedRef = useRef(false);
    var lastDropdownLocation = dropdownLocation;
    var internalSearchActive = searchable && !onSearch;
    var debouncedSearch = useCallback(function (value, typing) {
        if (isNilOrEmpty(onSearch))
            return;
        onSearch === null || onSearch === void 0 ? void 0 : onSearch({ id: id, value: value, textChange: typing });
    }, [id, onSearch]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    var updateForm = useCallback(inputChange(dispatch, customValidate), [customValidate]);
    var validateRequired = useCallback(function (value) {
        if (isNilOrEmpty(value)) {
            return {
                valid: false,
                errorMessage: requiredMessage !== null && requiredMessage !== void 0 ? requiredMessage : "".concat(label !== null && label !== void 0 ? label : 'This', " is required"),
            };
        }
        return { valid: true };
    }, [label, requiredMessage]);
    var getValidation = useCallback(function (value) {
        if (userManagedValidation && !validate)
            return { valid: valid, errorMessage: errorMessage };
        var validation = { valid: true };
        if (isRequired) {
            validation = validateRequired(value);
        }
        if (validate && validation.valid) {
            validation = validate(value, { id: id, label: label, required: isString(required) ? true : required !== null && required !== void 0 ? required : false });
        }
        return validation;
    }, [errorMessage, id, isRequired, label, required, userManagedValidation, valid, validate, validateRequired]);
    // timeoutId for debounce mechanism
    var resizeListener = useCallback(function () {
        if (timeoutId.current)
            clearTimeout(timeoutId.current);
        // change width from the state object after 150 milliseconds
        timeoutId.current = setTimeout(function () { return setListContainerStyles(getListContainerStyles()); }, 50);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    /**
     * close the menu if the input scrolls out of view
     */
    var scrollListener = useCallback(function (event) {
        var _a, _b;
        if (!((_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) && !((_b = selectListRef.current) === null || _b === void 0 ? void 0 : _b.contains(event.target))) {
            setIsOpen(false);
        }
    }, []);
    var callHandleOnChange = function (option) {
        handleOnChange({
            option: option,
            id: id,
            isMulti: isMulti,
            fixedOptions: fixedOptions,
            getValidation: getValidation,
            setValidation: setValidation,
            onChange: onChange,
            userManagedValidation: userManagedValidation,
            updateForm: updateForm,
            formInputId: formInputId,
            label: label,
            isLoaded: isLoaded,
            options: options,
        });
    };
    useEffect(function () {
        handleInitialValue();
        setIsLoaded(true);
        // did unmount
        return function () {
            cleanupFunction();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useValidationOnMount(id, label, value, userManagedValidation, getValidation, setValidation, updateForm);
    useUpdateValidation(form, formInputId, valid, errorMessage, _validation, setValidation);
    useEffect(function () {
        if (dropdownLocationBoundary > 0) {
            updateDropdownLocation();
            window.addEventListener('resize', updateDropdownLocation);
            return function () {
                window.removeEventListener('resize', updateDropdownLocation);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var updateDropdownLocation = function () {
        var _a, _b;
        if (isNotNilOrEmpty(wrapperRef.current)) {
            var componentRects = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
            var bottomMax = ((_b = componentRects === null || componentRects === void 0 ? void 0 : componentRects.bottom) !== null && _b !== void 0 ? _b : 0) + dropdownLocationBoundary;
            if (lastDropdownLocation === 'bottom' && bottomMax > window.innerHeight) {
                lastDropdownLocation = 'top';
                setDropdownLocation('top');
            }
            else if (lastDropdownLocation === 'top' && bottomMax < window.innerHeight) {
                lastDropdownLocation = 'bottom';
                setDropdownLocation('bottom');
            }
        }
    };
    useEffect(function () {
        if (!isActivated)
            return;
        setListDataArraySwitch(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [__isEmployee]);
    useEffect(function () {
        if (!isActivated)
            return;
        if (isOpen && filterTyping) {
            resetFocusRow({
                listDataArray: listDataArray,
                setFocusedRow: setFocusedRow,
                value: value,
                filterTyping: filterTyping,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, userManagedValidation]);
    useEffect(function () {
        if ((disabled || formDisabled) && isOpen) {
            setIsOpen(false);
        }
    }, [disabled, formDisabled, isOpen]);
    // if required changes after mount, revalidate
    useEffect(function () {
        var _a, _b, _c;
        var validation = getValidation(value);
        setValidation(validation);
        updateForm(formInputId, __assign(__assign({}, validation), { value: value, id: id, label: label, dirty: (_c = (_b = (_a = form === null || form === void 0 ? void 0 : form.inputs) === null || _a === void 0 ? void 0 : _a[formInputId]) === null || _b === void 0 ? void 0 : _b.dirty) !== null && _c !== void 0 ? _c : false }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [required]);
    useEffect(function () {
        if (!isActivated)
            return;
        // if the list isn't open and the user is typing something, open it!
        // NOTES: always open for isMulti; but for single/!isMulti ONLY open if no prior selection (otherwise it would always be open)
        if (isNotNilOrEmpty(inputValue) && (isMulti || (!isMulti && isNilOrEmpty(propsValue))) && !isOpen) {
            openMenu();
        }
        // this little gem indicates that native-search (searchable) is being used...
        if (internalSearchActive) {
            setFilterTyping((inputValue === null || inputValue === void 0 ? void 0 : inputValue.length) > 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue]);
    var onCreatedItemWithClose = useCallback(function (item) {
        if (closeListOnBlur)
            closeMenu();
        var createdItem = onCreatedItem === null || onCreatedItem === void 0 ? void 0 : onCreatedItem({ id: id, value: item.value });
        if (!userManagedValidation && createdItem) {
            var validation = getValidation(createdItem);
            setValidation(validation);
            var newValue = isMulti && isArray(value) ? __spreadArray(__spreadArray([], (value !== null && value !== void 0 ? value : []), true), [createdItem], false) : createdItem;
            updateForm(formInputId, __assign(__assign({}, validation), { value: newValue, id: id, label: label, dirty: true }));
        }
    }, [
        closeListOnBlur,
        formInputId,
        getValidation,
        id,
        isMulti,
        label,
        onCreatedItem,
        updateForm,
        userManagedValidation,
        value,
    ]);
    useEffect(function () {
        var _a;
        if (!isActivated)
            return;
        var _onCreatedItem = (onCreatedItem && onCreatedItem.length > 0 ? onCreatedItemWithClose : undefined);
        // update the searchable list
        if (listDataArraySwitch) {
            getListItemData(filterTyping, id, inputValue, isMulti, listDataArray, persistedInputValue, flattenedOptions, optionsDataArrayWithoutFixed, _onCreatedItem, setInputValue, setListDataArray, showOnlySelected, searchKeys);
            setListDataArraySwitch(false);
            // reset the position of the dropdown on render
            if (isOpen && ((_a = inputSelectContainer.current) === null || _a === void 0 ? void 0 : _a.getClientRects()[0].width) !== listContainerStyles.width) {
                setListContainerStyles(getListContainerStyles());
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listDataArraySwitch, inputValue]);
    useEffect(function () {
        if (!isLoaded)
            return;
        if (!isActivated) {
            activateSelect();
        }
        if (inputFocused) {
            if (onInputFocused) {
                onInputFocused({
                    id: id,
                });
            }
        }
        setCursorPosition();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputFocused]);
    useEffect(function () {
        if (!isLoaded)
            return;
        handleMultiToggle(isMulti);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMulti]);
    useEffect(function () {
        if (!isActivated)
            return;
        if (!isOpen) {
            setFocusedRow(null);
            setFilterTyping(false);
            if (persistedInputValue) {
                setPersistedInputValue('');
                setListDataArraySwitch(true);
            }
            // remove resize listener
            window.removeEventListener('resize', resizeListener);
            document.removeEventListener('scroll', scrollListener, true);
        }
        if (isOpen) {
            if (openScroll) {
                handleScrollToItemOnOpen({
                    gridListRef: gridListRef,
                    listDataArray: listDataArray,
                    listRef: listRef,
                    setFocusedRow: setFocusedRow,
                    value: value,
                    __isEmployee: __isEmployee,
                });
                setOpenScroll(false);
            }
            if (portal) {
                // set resize listener
                window.addEventListener('resize', resizeListener);
                document.addEventListener('scroll', scrollListener, true);
            }
        }
        // clean up function
        return function () {
            // remove resize listener
            window.removeEventListener('resize', resizeListener);
            document.removeEventListener('scroll', scrollListener, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    useEffect(function () {
        if (!isLoaded)
            return;
        var flatOpt = [];
        var flatClone = flattenedOptions.slice(0);
        if (isNilOrEmpty(propsValue) || propsValue === undefined) {
            // clear out all selected
            if (value && isNotNilOrEmpty(value)) {
                if (!Array.isArray(value) && value.optionsIndex !== undefined) {
                    var newObj = Object.assign({}, flatClone[value.optionsIndex]);
                    newObj.selected = false;
                    flatOpt.push(newObj);
                }
                else {
                    for (var i = 0; i < value.length; i++) {
                        var newObj = Object.assign({}, flatClone[value[i].optionsIndex]);
                        newObj.selected = false;
                        flatOpt.push(newObj);
                    }
                }
                setFlattenedOptions(flatOpt);
            }
            setValue(isMulti ? [] : undefined);
            setInputValue('');
            return;
        }
        // sometimes the incoming propsValue can be an obj or array, so treat accordingly
        if (!isMulti && isNotNilOrEmpty(propsValue)) {
            if (propsValue === null || propsValue === void 0 ? void 0 : propsValue.label) {
                setInputValue(propsValue.label);
            }
            else if (Array.isArray(propsValue)) {
                setInputValue(propsValue[0].label);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propsValue]);
    useEffect(function () {
        if (!isActivated && isLoaded)
            return;
        // the options or values (selected) have changed, so load 'em up!
        if (!isEqual(propsOptions, options) || !isEqual(propsValue, value)) {
            handleOptionsArrays({
                isActivated: isActivated,
                isMulti: isMulti,
                isLoaded: isLoaded,
                propsOptions: propsOptions,
                primaryKey: primaryKey,
                propsValue: propsValue,
                persistedInputValue: persistedInputValue,
                value: value,
                listDataArray: listDataArray,
                setOptionsDataArrayWithoutFixed: setOptionsDataArrayWithoutFixed,
                setFixedOptions: setFixedOptions,
                setOptions: setOptions,
                setListDataArray: setListDataArray,
                setValue: setValue,
                setFlattenedOptions: setFlattenedOptions,
                setIsActivated: setIsActivated,
            });
            // apply any search/filtering criteria (searchable ONLY!)
            if (filterTyping && internalSearchActive && (inputValue === null || inputValue === void 0 ? void 0 : inputValue.length) > 0) {
                handleInputOnSearch(inputValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propsOptions, propsValue]);
    var closeMenu = function () {
        setIsOpen(false);
    };
    var cleanupFunction = function () {
        closeMenu();
        setOptions([]);
        setFlattenedOptions([]);
        setFixedOptions([]);
        setOptionsDataArrayWithoutFixed([]);
        setIsActivated(false);
        // remove resize listener
        window.removeEventListener('resize', resizeListener);
        document.removeEventListener('scroll', scrollListener, true);
    };
    var activateSelect = function () {
        var flattened = flattenOptionsArray(propsOptions, primaryKey);
        var flatOpt = flattened.flattenedOptions;
        var val = getValueFromFlatArray(flatOpt, flattened.optionIndexMap, propsValue, primaryKey);
        if (isMulti) {
            if (val && isNotNilOrEmpty(val) && !isArray(val)) {
                setValue([val]);
            }
            else {
                setValue([]);
            }
        }
        if (!isMulti && val && !isArray(val)) {
            var ind = val.optionsIndex;
            flatOpt[ind].selected = true;
        }
        else if (isMulti && isNotNilOrEmpty(val) && isArray(val)) {
            for (var i = 0; i < val.length; i++) {
                var ind = val[i].optionsIndex;
                flatOpt[ind].selected = true;
            }
        }
        setFlattenedOptions(flatOpt);
        // this is ugly and a repeat of handleoptionsarray, but it prevents duplicate renders
        var optionsDataWithoutFixed = getOptionsWithoutFixed(flatOpt);
        var onlyFixedOptions = getOptionsOnlyFixed(flatOpt);
        setOptionsDataArrayWithoutFixed(optionsDataWithoutFixed);
        setFixedOptions(onlyFixedOptions);
        setOptions(flatOpt);
        if (!searchable && onSearch) {
            setListDataArraySwitch(true);
        }
        else {
            setListDataArray(isMulti ? optionsDataWithoutFixed : flatOpt);
        }
        setIsActivated(true);
    };
    var openMenu = function () {
        var valueCheck = '';
        if (!isMulti) {
            valueCheck = value && value.label ? value.label : '';
        }
        if (inputValue) {
            valueCheck = inputValue;
        }
        // We previously were doing an 'empty' search onBlur, which I assume was to clear out the
        // list of options when it was reopened. This had the additional side effect of causing async
        // searches to be run on every onBlur event. We are moving it to this function so that it can reset
        // the list when the menu is opened.
        // Also, don't execute the search for clearInputOnFocus and the focusedRef b/c the search/keypress
        // overrides the clearing of the inputValue in inputFunction's _handleInputFocused() (same update cycle)
        // However, we DO want to execute the search for single-mode when the value is set (ugh)
        if ((clearInputOnFocus && !isFocusedRef.current) ||
            (!clearInputOnFocus && !isMulti && isNotNilOrEmpty(valueCheck))) {
            handleInputOnSearch(valueCheck);
        }
        // if we haven't loaded yet populate flat options on open
        if (!isActivated) {
            activateSelect();
        }
        if (scrollToItemOnOpen) {
            setOpenScroll(true);
        }
        setListContainerStyles(getListContainerStyles());
        setIsOpen(true);
        // reset the focusRef so normal behavior continues with additional keypresses/events
        isFocusedRef.current = false;
    };
    /**
     * this will handle if users preselect grouped options, and populate the options if isMulti, this is so we can see the isFixed options on load
     */
    var handleInitialValue = function () {
        if (!isMulti && isNotNilOrEmpty(propsValue) && !isArray(propsValue)) {
            setValue(propsValue);
            var initialValue = propsValue ? propsValue.label : '';
            setInputValue(initialValue);
        }
        if (isMulti) {
            handleOptionsArrays({
                isActivated: isActivated,
                isMulti: isMulti,
                isLoaded: isLoaded,
                propsOptions: propsOptions,
                primaryKey: primaryKey,
                propsValue: propsValue,
                persistedInputValue: persistedInputValue,
                value: value,
                listDataArray: listDataArray,
                setOptionsDataArrayWithoutFixed: setOptionsDataArrayWithoutFixed,
                setFixedOptions: setFixedOptions,
                setOptions: setOptions,
                setListDataArray: setListDataArray,
                setValue: setValue,
                setFlattenedOptions: setFlattenedOptions,
                setIsActivated: setIsActivated,
            });
        }
    };
    var handleMultiToggle = function (isMulti) {
        var _onCreatedItem = (onCreatedItem && onCreatedItem.length > 0 ? onCreatedItemWithClose : undefined);
        // update the list item data if multi is toggled after intial load.
        getListItemData(filterTyping, id, inputValue, isMulti, listDataArray, persistedInputValue, flattenedOptions, optionsDataArrayWithoutFixed, _onCreatedItem, setInputValue, setListDataArray, showOnlySelected, searchKeys); // if we toggle to multi, change selection to array, if value, set that value to index 0 of new array, else set to empty array
        if (isMulti) {
            if (value && !Array.isArray(value)) {
                callHandleOnChange([value]);
                handleInputOnSearch('');
            }
            else {
                callHandleOnChange([]);
            }
            // if toggle to single and have an array with values, set selected to first available value, else, set to null.
        }
        else if (Array.isArray(value) && value.length) {
            callHandleOnChange(value[0]);
        }
        else {
            callHandleOnChange(undefined);
        }
    };
    /**
     * this will ensure the cursor stays at the beginning of the text field.  will ask about this later
     */
    var setCursorPosition = function () {
        if (inputFocused && (textInputRef === null || textInputRef === void 0 ? void 0 : textInputRef.current)) {
            textInputRef.current.setSelectionRange(0, 0);
        }
    };
    var handleInputOnSearch = function (inputValue) {
        _handleInputOnSearch({
            debouncedSearch: debouncedSearch,
            filterTyping: filterTyping,
            readOnly: readOnly,
            inputValue: inputValue,
            setInputValue: setInputValue,
            persistedInputValue: persistedInputValue,
            setPersistedInputValue: setPersistedInputValue,
            setFilterTyping: setFilterTyping,
            setFocusedRow: setFocusedRow,
            setListDataArraySwitch: setListDataArraySwitch,
            searchable: internalSearchActive,
            value: propsValue,
        });
    };
    var handleInputFocused = function (focused) {
        // set our focus-ref here because it's in the same cycle as the keypress event
        isFocusedRef.current = true;
        _handleInputFocused({
            clearInputOnFocus: clearInputOnFocus,
            focused: focused,
            inputValue: inputValue,
            setInputFocused: setInputFocused,
            setInputValue: setInputValue,
            value: propsValue,
        });
    };
    var callHandleRowClick = function (index, objToRemove) {
        handleRowClicked({
            closeListOnSelect: closeListOnSelect,
            closeMenu: closeMenu,
            handleInputOnSearch: handleInputOnSearch,
            handleOnChange: callHandleOnChange,
            index: index,
            isMulti: isMulti,
            inputValue: inputValue,
            persistentSearch: persistentSearch,
            options: options,
            setListDataArraySwitch: setListDataArraySwitch,
            setFilterTyping: setFilterTyping,
            setInputValue: setInputValue,
            setPersistedInputValue: setPersistedInputValue,
            searchable: internalSearchActive,
            textInputRef: textInputRef,
            value: value,
            objToRemove: objToRemove,
        });
    };
    /**
     * return the previous/next available focused row
     * @param param0 an object with the current focused row and direction to check
     */
    var getFocusedRow = function (_a) {
        var _b;
        var focusedRow = _a.focusedRow, direction = _a.direction;
        // compensate for the case in which the focusedRow is reset/null. It's evaluated as 0 which is incorrect (so set to -1)
        var _focusedRow = isNilOrEmpty(focusedRow) && focusedRow !== 0 && direction !== 'up' ? -1 : focusedRow;
        var numberCheck = direction === 'up' ? _focusedRow - 1 : _focusedRow + 1;
        if ((_b = listDataArray[_focusedRow]) === null || _b === void 0 ? void 0 : _b.header) {
            return getFocusedRow({ focusedRow: numberCheck, direction: direction });
        }
        if (!listDataArray[numberCheck]) {
            return _focusedRow;
        }
        // if theres a next row and its not disabled and (not a header && not multi)
        if (listDataArray[numberCheck] && !listDataArray[numberCheck].disabled) {
            if (!isMulti && listDataArray[numberCheck].header) {
                return getFocusedRow({ focusedRow: numberCheck, direction: direction });
            }
            return numberCheck;
        }
        // else keep searching up and down till we find one. if we dont find one itll return the current focused row;
        return getFocusedRow({ focusedRow: numberCheck, direction: direction });
    };
    var handleListKeypress = function (e) {
        var _a;
        e.stopPropagation();
        // eslint-disable-next-line react-hooks/rules-of-hooks
        var upArrowPressed = useKeyPress(e, upKeys);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        var downArrowPressed = useKeyPress(e, downKeys);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        var directionArrowsPressed = useKeyPress(e, directionArrows);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        var activatePressed = useKeyPress(e, activateButtons);
        // eslint-disable-next-line react-hooks/rules-of-hooks
        var closeButtonsPressed = useKeyPress(e, closeButtons);
        var mouseDown = e.type === 'mousedown';
        // eslint-disable-next-line react-hooks/rules-of-hooks
        var spaceBarPress = useKeyPress(e, spaceKeys);
        var activatedKeyPressed = activatePressed || spaceBarPress;
        if (directionArrowsPressed) {
            e.preventDefault();
            return;
        }
        var scrollRef = listRef;
        var scrollToSelected = function (newFocusedRow) {
            var _a;
            if (newFocusedRow !== focusedRow) {
                (_a = scrollRef.current) === null || _a === void 0 ? void 0 : _a.scrollToItem(newFocusedRow);
            }
        };
        if (upArrowPressed) {
            e.preventDefault();
            var newFocusedRow = getFocusedRow({ focusedRow: focusedRow, direction: 'up' });
            setFocusedRow(newFocusedRow);
            scrollToSelected(newFocusedRow);
            return;
        }
        if (downArrowPressed) {
            e.preventDefault();
            if (!isOpen) {
                openMenu();
                setFocusedRow(null);
            }
            else {
                var newFocusedRow = getFocusedRow({ focusedRow: focusedRow, direction: 'down' });
                setFocusedRow(newFocusedRow);
                scrollToSelected(newFocusedRow);
            }
            return;
        }
        if (isOpen && activatePressed) {
            // do nothing if this is an empty row or a disabled row or is a header and not multi --  we should never have to do this based on the focused row search
            if (listDataArray.length === 0 ||
                isNilOrEmpty(focusedRow) ||
                listDataArray[focusedRow].empty ||
                listDataArray[focusedRow].disabled ||
                (listDataArray[focusedRow].header && !isMulti)) {
                return;
            }
            // if clicked on creatable
            if (listDataArray[focusedRow].creatable && onCreatedItem) {
                setInputValue('');
                onCreatedItemWithClose({ id: id, value: inputValue });
                return;
            }
            // prevent submits for enterkey and other events
            e.preventDefault();
            var clickIndex = options.findIndex(function (x) { return isEqual(x, listDataArray[focusedRow]); });
            callHandleRowClick(clickIndex);
            return;
        }
        if (closeButtonsPressed && isOpen) {
            e.target.blur();
            // always reset focus for blur
            isFocusedRef.current = false;
            setInputFocused(false);
            closeMenu();
            return;
        }
        if (!isOpen && (activatedKeyPressed || (openOnMouseDown && mouseDown))) {
            openMenu();
            return;
        }
        // this will prevent mousedown blurs when selecting an item
        if (mouseDown && !((_a = textInputRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
            e.preventDefault();
            return;
        }
    };
    var handleListBlur = function (e) {
        var _a;
        // always reset focus for blur
        isFocusedRef.current = false;
        if (closeListOnBlur &&
            !((_a = inputSelectContainer.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget)) &&
            e.relatedTarget !== selectListRef.current) {
            closeMenu();
            // only reset the inputs if persistentSearch is NOT set
            if (!persistentSearch) {
                resetInputValue({
                    inputValue: inputValue,
                    isMulti: isMulti,
                    setInputValue: setInputValue,
                    setFilterTyping: setFilterTyping,
                    setPersistedInputValue: setPersistedInputValue,
                    value: propsValue,
                });
            }
        }
        setListDataArraySwitch(true);
        if (isNotNilOrEmpty(onBlur)) {
            onBlur === null || onBlur === void 0 ? void 0 : onBlur({ id: id });
        }
    };
    var handleClearButtonClicked = function (value) {
        handleInputOnSearch(value);
        if (isMulti) {
            return callHandleOnChange([]);
        }
        return callHandleOnChange(undefined);
    };
    var handleShowClearbutton = function () {
        return showClearButton &&
            ((isMulti && value && Array.isArray(value) && value.filter(function (x) { return !x.isFixed; }).length > 0) ||
                (!isMulti && (inputValue === null || inputValue === void 0 ? void 0 : inputValue.length) > 0));
    };
    var dropdownListShadowStylesObj = isCitron ? dropdownListShadowStylesCitrus2022 : dropdownListShadowStyles;
    var dropdownListShadowSelectAllStylesObj = isCitron
        ? dropdownListShadowSelectAllStylesCitrus2022
        : dropdownListShadowSelectAllStyles;
    var emotionObj = (_b = {},
        _b[emotionCss(dropdownListShadowStylesObj)] = true,
        _b[emotionCss(dropdownListShadowSelectAllStylesObj)] = enableSelectAll,
        _b['pcty-input-select__menu-list'] = true,
        _b);
    var dropdownListStyles = cx(emotionObj);
    var styleNames = {
        dropdownListShadowStylesObj: emotionObj[emotionCss(dropdownListShadowStylesObj)],
        dropdownListShadowSelectAllStylesObj: emotionObj[emotionCss(dropdownListShadowSelectAllStylesObj)],
    };
    /**
     * these options will be spread over the fixed list and grid list
     */
    var commonListProps = __assign({ innerRef: scrollingListInnerRef, outerRef: scrollingListOuterRef, onScroll: function (e) {
            return handleListScroll(e, id, setScrollLoading, scrollLoading, scrollingListInnerRef, scrollingListOuterRef, scrolledToBottomItemBuffer, rowHeight, onScrolledToBottom ? onScrolledToBottom : undefined);
        }, className: dropdownListStyles, useIsScrolling: isScrollingPlaceholder }, (sessionStorage.getItem('citrus-debug') === 'true' ? { 'data-style': getStyleNames(styleNames) } : {}));
    /**
     * this handles dropdownlist size so if we filter, and there are fewer options than the max size, reduce the height of the list
     */
    var getListSize = useCallback(function () {
        var trueRowHeight = isCitron ? rowHeight + citrus2022RowPadding * 2 : rowHeight;
        var maxSize = optionsToDisplay * trueRowHeight;
        var currentListSize = listDataArray.length * trueRowHeight;
        return currentListSize <= maxSize ? currentListSize : maxSize;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [listDataArray.length]);
    var applyNonLegacyListItemStyles = function (style, index) {
        style.left = "".concat(citrus2022RowPadding, "px");
        style.width = "calc(100% - ".concat(citrus2022RowPadding * 2, "px)");
        style.top = index * (rowHeight + citrus2022RowPadding);
    };
    // FIXME: Figure out wtf is happening in react-window
    var FixedSizeListTyped = FixedSizeList;
    var getFixedList = function () { return (React.createElement(FixedSizeListTyped, __assign({}, commonListProps, { height: getListSize(), initialScrollOffset: initialScrollOffset, itemCount: listDataArray.length, itemData: listDataArray, itemSize: rowHeight + (isCitron ? citrus2022RowPadding : 0), key: 'pcty-input-select__menu-list', overscanCount: 50, ref: listRef, width: '100%' }), function (_a) {
        var index = _a.index, style = _a.style, data = _a.data, isScrolling = _a.isScrolling;
        if (isCitron) {
            applyNonLegacyListItemStyles(style, index);
        }
        return (React.createElement(FixedSizeListRow, { data: data, id: id, index: index, isFocused: focusedRow === index, isMulti: isMulti, isScrolling: isScrolling, isScrollingPlaceholderText: isScrollingPlaceholderText, onChange: callHandleRowClick, renderItem: renderItem, style: style }));
    })); };
    var getEmployeeList = function () { return (React.createElement(FixedSizeListTyped, __assign({}, commonListProps, { height: getListSize(), initialScrollOffset: initialScrollOffset, itemCount: listDataArray.length, itemSize: rowHeight + (isCitron ? citrus2022RowPadding : 0), key: 'pcty-input-select__menu-list', overscanCount: 50, ref: listRef, width: '100%' }), function (_a) {
        var index = _a.index, style = _a.style, isScrolling = _a.isScrolling;
        var dataObject = listDataArray[index];
        if (isCitron) {
            applyNonLegacyListItemStyles(style, index);
        }
        return (React.createElement(FixedSizeGridRow, { data: dataObject, id: id, index: index, isFocused: focusedRow === index, isMulti: isMulti, isScrolling: isScrolling, isScrollingPlaceholderText: isScrollingPlaceholderText, onChange: callHandleRowClick, renderAvatar: props.renderAvatar, style: style }));
    })); };
    var getDropdownList = function () {
        if (__isEmployee) {
            return getEmployeeList();
        }
        return getFixedList();
    };
    var getHidePlaceholder = function () {
        return isMulti && isLoaded && (isNotNilOrEmpty(value) || isNotNilOrEmpty(fixedOptions));
    };
    /**
     * this handles the options list portaling styles.
     */
    var getListContainerStyles = useCallback(function () {
        var _a;
        var inputRefRects = (_a = inputSelectContainer.current) === null || _a === void 0 ? void 0 : _a.getClientRects()[0];
        var retStyles = {
            backgroundColor: '#ffffff',
        };
        if (inputRefRects === undefined || !portal) {
            return retStyles;
        }
        retStyles.width = inputRefRects.width;
        // this adds window.scrollY for position absolute...
        // retStyles['top'] = inputRefRects.top + inputRefRects.height + window.scrollY;
        retStyles.top = inputRefRects.top + (_dropdownLocation === 'top' ? 0 : inputRefRects.height);
        if (_dropdownLocation === 'top') {
            retStyles.transform = 'translateY(-100%)';
        }
        retStyles.left = inputRefRects.left;
        retStyles.zIndex = 9999;
        return retStyles;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputSelectContainer.current, getListSize(), _dropdownLocation]);
    /**
     * the container of the dropdownlist. this will be portalled to the bottom of the document.body by default
     */
    var getDropdownListContainer = function () {
        if (!isOpen || Object.keys(listContainerStyles).length === 0)
            return null;
        var template = (React.createElement(DropdownListContainer, __assign({}, getAutomation(automationId, "input-select-".concat(dropDownListContainerSuffix)), { id: dropDownListContainerId, ref: selectListRef, style: __assign({}, listContainerStyles), tabIndex: -1 }),
            isArray(value) && enableSelectAll && (React.createElement(SelectAllButton, { automationId: automationId, deselectAllOptionText: deselectAllOptionText, deselectFilteredOptionText: deselectFilteredOptionText, filterTyping: filterTyping, handleListKeypress: handleListKeypress, handleOnChange: callHandleOnChange, inputValue: inputValue, isMulti: isMulti, listDataArray: listDataArray, searchable: internalSearchActive, selectAllOptionText: selectAllOptionText, selectFilteredOptionText: selectFilteredOptionText, setFilterTyping: setFilterTyping, setInputValue: setInputValue, setListDataArraySwitch: setListDataArraySwitch, setPersistedInputValue: setPersistedInputValue, textInputRef: textInputRef, value: value })),
            getDropdownList()));
        if (!portal) {
            return template;
        }
        return React.createElement(React.Fragment, null, ReactDOM.createPortal(template, document.body));
    };
    return (React.createElement(Input, { automationId: automationId, className: classNameWrapper, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, emptyLabel: emptyLabel, errorMessage: _validation.errorMessage, footnote: footnote, hideOptionalLabel: hideOptionalLabel, id: id, label: label, labelAddOn: labelAddOn, labelPosition: labelPosition, readOnly: readOnly, ref: wrapperRef, required: isRequired, showRequiredLabel: showRequiredLabel, showValidationState: showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState, tooltip: tooltip, type: "dropdown-wrapper", valid: _validation.valid },
        React.createElement(InputBaseMainContainerStyles, __assign({ className: classNameWrapper, onBlur: function (e) { return handleListBlur(e); }, ref: inputSelectContainer, style: wrapperStyles }, getAutomation(automationId, 'input-select-main-container')),
            React.createElement(InputBase, __assign({}, getAutomation(automationId, 'input-base'), { actionAreaRef: actionArea, actionIconSeparator: actionIconSeparator, "aria-expanded": isOpen, "aria-haspopup": "listbox", "aria-label": ariaLabel, "aria-owns": dropDownListContainerId, className: className !== null && className !== void 0 ? className : '', css: props.css, debounceTimeout: debounceTimeout, disabled: disabled !== null && disabled !== void 0 ? disabled : formDisabled, hidePlaceholder: getHidePlaceholder(), id: id, isLoading: isLoading, maxLength: maxLength, onChange: function (_a) {
                    var value = _a.value;
                    return handleInputOnSearch(value);
                }, onClearButton: function (_a) {
                    var value = _a.value;
                    return handleClearButtonClicked(value);
                }, onInputFocus: function (focused) { return handleInputFocused(focused); }, onKeyDown: function (e) { return handleListKeypress(e); }, placeholder: placeholder, readOnly: readOnly, ref: textInputRef, renderActionIcon: function (config) {
                    var _a;
                    return (React.createElement(DropdownIcon, { closeMenu: closeMenu, config: config, disabled: (_a = disabled !== null && disabled !== void 0 ? disabled : formDisabled) !== null && _a !== void 0 ? _a : false, isOpen: isOpen, openMenu: openMenu }));
                }, renderInputActionArea: function () {
                    var _a;
                    return (React.createElement(MultiTagsElement, { actionAreaRef: actionArea, automationId: automationId, disabled: (_a = disabled !== null && disabled !== void 0 ? disabled : formDisabled) !== null && _a !== void 0 ? _a : false, fixedOptions: fixedOptions, handleRowClick: callHandleRowClick, id: id, isMulti: isMulti, options: options, readOnly: readOnly !== null && readOnly !== void 0 ? readOnly : false, value: value }));
                }, required: isRequired, showClearButton: handleShowClearbutton(), tabIndex: tabIndex, valid: !(showValidationState !== undefined ? showValidationState : form === null || form === void 0 ? void 0 : form.showValidationState) || _validation.valid, value: inputValue })),
            getDropdownListContainer())));
};
export default InputSelectBase;
